import * as React from 'react';
import {useCallback, useMemo, useState, useEffect} from 'react';
import makeComp from '../../util/profiler';
import {ThemeProvider} from 'styled-components';
import {Button} from 'semantic-ui-react';
import * as QueryEditorStyles from './editors/QueryEditor.styles';
import * as CG from './cg/graph';
import * as Op from './cg/ops';
import * as HL from './cg/hl';
import * as ExpressionEditor from './ExpressionEditor';
import {ComputeGraphViz} from './ComputeGraphViz';
import {getPanelStacksForType} from './availablePanels';
import {PanelComp2} from './PanelComp';
import PanelNameEditor from './editors/PanelNameEditor';
import * as Code from './cg/code';
import * as Update from '../../util/update';
import * as LLReact from './cg/cgreact';
import * as Types from './types';

// export const EditorFromScratch: React.FC<{}> = makeComp(
//   props => {
//     const root = LL.voidNode();

//     const [panelId, setPanelId] = useState<string | undefined>(undefined);
//     const [node, setNode] = useState<LL.Node>(root);
//     const [panelConfig, setPanelConfig] = useState<any>({});
//     const updatePanelConfig = useCallback((newConfig: any) => {
//       setPanelConfig((curConfig: any) => ({...curConfig, ...newConfig}));
//     }, []);
//     const [panelContext, setPanelContext] = useState<any>({});
//     const updatePanelContext = useCallback<any>((newContext: any) => {
//       setPanelContext((curContext: any) => ({...curContext, ...newContext}));
//     }, []);

//     const handlerStacks = getTypeHandlerStacks(node.type);
//     const stackIds = handlerStacks.map(PanelLib.getStackIdAndName);
//     const stackIndex = stackIds.findIndex(si => si.id === panelId);

//     const curPanelId =
//       stackIndex !== -1
//         ? stackIds[stackIndex].id
//         : stackIds.length > 0
//         ? stackIds[0].id
//         : undefined;
//     const handler =
//       stackIndex !== -1 ? handlerStacks[stackIndex] : handlerStacks[0];

//     const value = node.nodeType !== 'void' ? LL.execute(node) : undefined;

//     return (
//       <ThemeProvider theme={QueryEditorStyles.themes.carey}>
//         <div style={{padding: 32, display: 'flex'}}>
//           <div style={{flex: 1}}>
//             <Header>Expression</Header>
//             <ExpressionEditor.NodeEditor node={node} updateNode={setNode} />
//             <Header>Type</Header>
//             <pre style={{fontSize: 12}}>{Types.toString(node.type)}</pre>
//             <Header>Result</Header>
//             {node.nodeType !== 'void' && (
//               <pre style={{fontSize: 12}}>
//                 {JSON.stringify(LL.execute(node), undefined, 2)}
//               </pre>
//             )}
//             <Header>Panel</Header>
//             {curPanelId != null && (
//               <WBSearchableSelect
//                 infiniteScroll
//                 options={stackIds.map(si => ({
//                   name: si.displayName,
//                   value: si.id,
//                 }))}
//                 value={curPanelId}
//                 onSelect={val => setPanelId(val as string)}
//               />
//             )}
//             {handler != null ? (
//               <PanelComp2
//                 input={{path: null as any, value}}
//                 inputType={node.type}
//                 loading={false}
//                 panelSpec={handler}
//                 configMode={false}
//                 context={panelContext}
//                 config={panelConfig}
//                 updateConfig={updatePanelConfig}
//                 updateContext={updatePanelContext}
//               />
//             ) : (
//               <div>No panel for type</div>
//             )}
//           </div>
//           <div style={{flex: 1}}>
//             <ComputeGraphViz node={node} width={600} height={600} />
//           </div>
//         </div>
//       </ThemeProvider>
//     );
//   },
//   {id: 'ComputeGraphDemo'}
// );

export const AssignmentEditor: React.FC<{
  assignment: Code.Assignment;
  updateAssignment(newAssignment: Code.Assignment): void;
}> = makeComp(
  // TODO: What to do if inputType is no longer valid for a step?
  ({assignment, updateAssignment}) => {
    return (
      <div>
        {assignment.name}
        {' = '}
        <ExpressionEditor.ExpressionEditor
          focusOnMount={true}
          node={assignment.node}
          updateNode={newNode => {
            console.log('NEW NODE FOR ASSIGNEMNT', assignment.name, newNode);
            updateAssignment({...assignment, node: newNode});
          }}
        />
      </div>
    );
  },
  {id: 'NodeEditor'}
);

interface Panel {
  panelId: string;
  config: any;
  // inputVariable: Code.Assignment;
}

interface Block {
  rows: Array<Panel | Code.Assignment>;
}

function blockRowIsAssignment(
  blockRow: Panel | Code.Assignment
): blockRow is Code.Assignment {
  return (blockRow as Code.Assignment).node != null;
}

function nextVarName(block: Block): string {
  let varName = 'a';
  for (let i = 0; i < 26; i++) {
    let found = false;
    for (const br of block.rows) {
      if (blockRowIsAssignment(br) && br.name === varName) {
        found = true;
        break;
      }
    }
    if (!found) {
      return varName;
    }
    varName = String.fromCharCode(varName.charCodeAt(0) + 1);
  }
  return varName;
}

export const EditorPanel: React.FC<{
  varName: string;
  node: CG.Node;
  panelId: string;
  config: any;
  panelContext: any;
  updatePanelId(newPanelId: string): void;
  updateConfig(newConfig: any): void;
  updatePanelContext(newContext: any): void;
}> = makeComp(
  ({
    node,
    varName,
    panelId,
    config,
    panelContext,
    updatePanelId,
    updateConfig,
    updatePanelContext,
  }) => {
    const {curPanelId, stackIds, handler} = useMemo(
      () => getPanelStacksForType(node.type, panelId),
      [node.type, panelId]
    );

    return curPanelId == null ? (
      <div>No panel for type</div>
    ) : (
      <div style={{marginTop: 16}}>
        {curPanelId != null && (
          <>
            <PanelNameEditor
              autocompleteOptions={stackIds.map(si => ({
                name: si.displayName,
                value: si.id,
              }))}
              value={curPanelId}
              setValue={val => updatePanelId(val as string)}
            />
            ({varName})
          </>
        )}
        {handler != null && (
          <PanelComp2
            input={{path: node}}
            inputType={node.type}
            loading={false}
            panelSpec={handler}
            configMode={false}
            context={panelContext}
            config={config}
            updateConfig={updateConfig}
            updateContext={updatePanelContext}
          />
        )}
      </div>
    );
  },
  {id: 'EditorPanel'}
);

// function fasionSweep() {
//   const entityName = Op.constString('shawn');
//   const projectName = Op.constString('fasion-sweep');
//   return Op.opRootProject({entityName, projectName});
// }

// function artifactWithTables() {
//   return Op.opRootArtifact({
//     entityName: Op.constString('shawn'),
//     projectName: Op.constString('dsviz_demo'),
//     artifactTypeName: Op.constString('dataset'),
//     artifactId: Op.constString('train_results:v3'),
//   });
// }

// function artifactTable() {
//   return Op.opArtifactTable({
//     artifact: Op.opRootArtifact({
//       entityName: Op.constString('shawn'),
//       projectName: Op.constString('dsviz_demo'),
//       artifactTypeName: Op.constString('dataset'),
//       artifactId: Op.constString('train_results:v3'),
//     }),
//     path: Op.constString('train_iou_score_table.table.json'),
//   });
// }

export const EditorFromScratch2: React.FC<{}> = makeComp(
  props => {
    const assignButtonRef = React.useRef<Button>(null);
    const [block, setBlock] = useState<Block>({rows: []});
    const [panelContext, setPanelContext] = useState<any>({});
    const updatePanelContext = useCallback<any>((newContext: any) => {
      setPanelContext((curContext: any) => ({...curContext, ...newContext}));
    }, []);

    useEffect(() => {
      assignButtonRef.current?.focus();
    }, []);

    let curFrame = Code.newCodeBlock();

    let lastOutput: HL.EditingNode = CG.voidNode();
    let lastVarname = 'null';

    const children: JSX.Element[] = [];
    for (let i = 0; i < block.rows.length; i++) {
      const blockRow = block.rows[i];
      if (blockRowIsAssignment(blockRow)) {
        children.push(
          <AssignmentEditor
            key={i}
            assignment={blockRow}
            updateAssignment={newAssignment =>
              setBlock({
                ...block,
                rows: Update.updateArrayIndex(block.rows, i, newAssignment),
              })
            }
          />
        );
        // Add variables to frame as we go, so downward cells can access
        curFrame = {
          ...curFrame,
          statements: [...curFrame.statements, blockRow],
        };
        lastOutput = blockRow.node;
        lastVarname = blockRow.name;
      } else {
        const {panelId, config} = blockRow;
        children.push(
          lastOutput.nodeType !== 'output' ? (
            <div>-</div>
          ) : (
            <EditorPanel
              key={i}
              node={lastOutput as CG.Node}
              varName={lastVarname}
              panelId={panelId}
              config={config}
              panelContext={panelContext}
              updatePanelId={newPanelId =>
                setBlock({
                  ...block,
                  rows: Update.updateArrayIndex(block.rows, i, {
                    ...blockRow,
                    panelId: newPanelId,
                  }),
                })
              }
              updateConfig={newConfig =>
                setBlock({
                  ...block,
                  rows: Update.updateArrayIndex(block.rows, i, {
                    ...blockRow,
                    config: {
                      ...blockRow.config,
                      ...newConfig,
                    },
                  }),
                })
              }
              updatePanelContext={updatePanelContext}
            />
          )
        );
      }
    }

    return (
      <LLReact.ComputeGraphContextProvider>
        <ThemeProvider theme={QueryEditorStyles.themes.carey}>
          <div style={{padding: 32, display: 'flex'}}>
            <div style={{flex: 1}}>
              {children}
              <div style={{marginTop: 16}}>
                <Button
                  ref={assignButtonRef}
                  onClick={() =>
                    setBlock({
                      rows: [
                        ...block.rows,
                        // {
                        //   name: nextVarName(block),
                        //   node: Op.opProjectRuns({
                        //     project: fasionSweep(),
                        //   }) /* LL.voidNode() */,
                        // },
                        // {
                        //   name: nextVarName(block),
                        //   node: fasionSweep(),
                        // },
                        {
                          name: nextVarName(block),
                          node: CG.varNode(Types.array('number'), 'a'),
                        },
                        // {
                        //   name: nextVarName(block),
                        //   node: Op.constNumber(5),
                        // },
                        // {
                        //   name: nextVarName(block),
                        //   node: artifactWithTables(),
                        // },
                        // {
                        //   name: nextVarName(block),
                        //   node: artifactTable(),
                        // },
                      ],
                    })
                  }>
                  Assignment
                </Button>
                {lastOutput.nodeType !== 'void' && (
                  <Button
                    onClick={() =>
                      setBlock({
                        rows: [...block.rows, {panelId: '', config: {}}],
                      })
                    }>
                    Panel
                  </Button>
                )}
              </div>
            </div>
            <div style={{flex: 1}}>
              <pre>{JSON.stringify(block, undefined, 2)}</pre>
              {/* <ComputeGraphViz node={node} width={600} height={600} /> */}
            </div>
          </div>
        </ThemeProvider>
      </LLReact.ComputeGraphContextProvider>
    );
  },
  {id: 'ComputeGraphDemo'}
);

export const InterestingDag: React.FC<{}> = makeComp(
  props => {
    const entityName = Op.constString('shawn');
    const projectName = CG.varNode('string', 'a');
    const project = Op.opRootProject({entityName, projectName});
    const projectStars = Op.opProjectStars({project});

    const runs = Op.opProjectRuns({project});
    const runsStars = Op.opRunsStars({runs});

    const avgRunStars = Op.opNumbersAvg({from: runsStars});

    const avgProjectStarsMinusAvgRunStars = Op.opNumberSub({
      lhs: projectStars,
      rhs: avgRunStars,
    });

    return (
      <ComputeGraphViz
        node={avgProjectStarsMinusAvgRunStars}
        width={600}
        height={600}
      />
    );
  },
  {id: 'ComputeGraphDemo'}
);
